import { Button, DatePicker, Link } from "@heroui/react";
import { useParams } from "react-router-dom";
import { NewUserHandbook } from "@/app/components/handbooks/userHandbook.tsx";
import { useState, useEffect, useCallback } from "react";
import { LockLog, LockLogRecordType } from "@/api/locks";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { UserChip } from "@/app/components/chips/userChip.tsx";
import { FaLockOpen } from "react-icons/fa6";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { AdminChip } from "@/app/components/chips/adminChip";
import { LandlordHandbook } from "@/app/components/handbooks/landlordHandbook";
import { parseDate } from "@internationalized/date";
import { normalizeDate } from "@/utils";
import { I18nProvider } from "@react-aria/i18n";
import { LoadingSpinner } from "@/app/components/loadingSpinner";

export function LockLogsPage() {
  const { id } = useParams();

  const [data, setData] = useState<LockLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleError = useErrorHandling();

  const [activeUser, setActiveUser] = useState<number | null>(null);
  const [activeAdminUser, setActiveAdminUser] = useState<number | null>(null);
  const [activeDateFrom, setActiveDateFrom] = useState<string | null>(null);
  const [activeDateTo, setActiveDateTo] = useState<string | null>(null);

  const resetFilters = useCallback(() => {
    setActiveDateFrom(null);
    setActiveDateTo(null);
    setActiveUser(null);
    setActiveAdminUser(null);
  }, []);

  useEffect(() => {
    if (activeDateFrom == null || activeDateTo == null) {
      setData([]);
      return;
    }

    setIsLoading(true);
    api.locks
      .getLogs(
        Number(id),
        0,
        10,
        activeUser!,
        activeAdminUser!,
        activeDateFrom!,
        activeDateTo!,
      )
      .then((res) => {
        console.log(res.data.length);
        setData(res.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    activeUser,
    activeAdminUser,
    activeDateFrom,
    activeDateTo,
    id,
    handleError,
  ]);

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex flex-col gap-2 max-md:flex-grow">
          <Link
            onClick={() => {
              api.locks
                .unlock(Number(id))
                .then(() => toast.success("Замок успешно открыт!"));
            }}
            className="flex flex-row gap-2 items-center p-4 rounded-xl font-medium text-foreground-600 cursor-pointer"
          >
            <FaLockOpen />
            Открыть замок
          </Link>
          <Link
            href={`/dashboard/locks/${id}`}
            className="flex flex-row items-center p-4 rounded-xl font-medium text-primary bg-primary bg-opacity-10"
          >
            Журнал действий
          </Link>
          <Link
            href={`/dashboard/locks/${id}/passcodes`}
            className="flex flex-row items-center p-4 rounded-xl font-medium text-foreground-600"
          >
            Пароли
          </Link>
        </div>
        <div className="flex-grow flex flex-col gap-2">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            <div className="flex flex-row w-full items-center">
              <div className="flex flex-row justify-between w-full gap-4 items-center">
                <span className="font-medium">Журнал действий</span>
              </div>
            </div>
            <div className="flex flex-row gap-2 items-center max-w-fit">
              {/* @ts-ignore */}
              <LandlordHandbook
                errorMessage={""}
                isInvalid={false}
                onSelectionChange={(key: any) => setActiveAdminUser(key)}
                selectedKey={activeAdminUser}
              />

              {/* @ts-ignore */}
              <NewUserHandbook
                errorMessage={""}
                isInvalid={false}
                onSelectionChange={(key: any) => setActiveUser(key)}
                selectedKey={activeUser}
              />

              <I18nProvider locale="ru-RU">
                <DatePicker
                  label="От"
                  variant="bordered"
                  isRequired
                  onChange={(date) =>
                    setActiveDateFrom(date?.toString() || null)
                  }
                  value={
                    activeDateFrom
                      ? parseDate(normalizeDate(activeDateFrom))
                      : undefined
                  }
                />
              </I18nProvider>

              <I18nProvider locale="ru-RU">
                <DatePicker
                  label="До"
                  variant="bordered"
                  isRequired
                  onChange={(date) => setActiveDateTo(date?.toString() || null)}
                  value={
                    activeDateTo
                      ? parseDate(normalizeDate(activeDateTo))
                      : undefined
                  }
                />
              </I18nProvider>
            </div>
            <div className="flex flex-row gap-2 max-w-fit">
              <Button
                size="sm"
                variant="flat"
                color="primary"
                onClick={resetFilters}
              >
                Сбросить
              </Button>
            </div>
            <TableBuilder
              removeWrapper
              displaySearch={false}
              displayOptions={false}
              defaultSortDescriptor={{
                column: "created_at",
                direction: "descending",
              }}
              columns={[
                {
                  type: ColumnType.Custom,
                  key: "user",
                  label: "Пользователь",
                  render(_value, row: LockLog) {
                    if (row.record_type == 3 || row.record_type == 12) {
                      if (row.admin_id) {
                        return <AdminChip adminId={row.admin_id} />;
                      } else if (row.user_id) {
                        return <UserChip userId={row.user_id} />;
                      } else {
                        return "Неизвестно";
                      }
                    }

                    return row.username;
                  },
                },
                {
                  type: ColumnType.Custom,
                  key: "record_type",
                  label: "Тип",
                  render(value) {
                    switch (value) {
                      case LockLogRecordType.FINGERPRINT_UNLOCK:
                        return "Отпечаток";
                      case LockLogRecordType.PASSCODE_UNLOCK:
                        return "Пароль";
                      case LockLogRecordType.IC_CARD_UNLOCK:
                        return "Карта";
                      case LockLogRecordType.GATEWAY_UNLOCK:
                      case LockLogRecordType.SECOND_GATEWAY_UNLOCK:
                        return "Elastic";
                      default:
                        return "Неизвестно";
                    }
                  },
                },
                {
                  type: ColumnType.DateTime,
                  key: "created_at",
                  label: "Дата и время",
                  sortable: true,
                },
              ]}
              data={data || []}
              rowsPerPage={15}
            />
          </div>
        </div>
      </div>
    </>
  );
}
